import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";
import { useEffect, useState } from "react";
import {
  ActionIcon, Badge, Button, Container, Drawer, Group, Modal, NumberInput,
  Stack, Switch, Table, Tabs, Text, TextInput, Textarea, Title, Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconPencil, IconTrash, IconPackageImport, IconAdjustments, IconHistory, IconRefresh, IconPrinter } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { StockItem, StockMovement } from "../api/types";
import RecipesTab from "./stock/RecipesTab";
import ConsumptionTab from "./stock/ConsumptionTab";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useLocalAgent } from "../hooks/useLocalAgent";
import { localAgentPrint } from "../api/localAgent";

const EMPTY: Partial<StockItem> = { name: "", unit: "buc", quantity: 0, low_threshold: null, notes: "", active: true };

export default function Stock() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const isAdmin = user.role === "admin";
  const canEdit = isAdmin || user.role === "operator";
  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [editOpen, editH] = useDisclosure(false);
  const [draft, setDraft] = useState<Partial<StockItem>>(EMPTY);

  const [moveItem, setMoveItem] = useState<StockItem | null>(null);
  const [moveMode, setMoveMode] = useState<"restock" | "adjust">("restock");
  const [moveQty, setMoveQty] = useState<number>(0);
  const [moveNote, setMoveNote] = useState("");
  const [moveOpen, moveH] = useDisclosure(false);

  const [histItem, setHistItem] = useState<StockItem | null>(null);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [histOpen, histH] = useDisclosure(false);

  const localAgent = useLocalAgent();
  const [printItem, setPrintItem] = useState<StockItem | null>(null);
  const [printCopies, setPrintCopies] = useState<number | "">(1);
  const [printing, setPrinting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.listStock();
      setItems(r.items);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  function itemActions(it: StockItem) {
    return (
      <Group gap={4} wrap="nowrap">
        <Tooltip label={t("stock.restock")}><ActionIcon variant="subtle" onClick={() => openMove(it, "restock")}><IconPackageImport size={18} /></ActionIcon></Tooltip>
        <Tooltip label={t("stock.adjust")}><ActionIcon variant="subtle" onClick={() => openMove(it, "adjust")}><IconAdjustments size={18} /></ActionIcon></Tooltip>
        {canEdit && <Tooltip label={t("stock.printLabel")}><ActionIcon variant="subtle" onClick={() => openPrint(it)}><IconPrinter size={18} /></ActionIcon></Tooltip>}
        <Tooltip label={t("stock.history")}><ActionIcon variant="subtle" onClick={() => void openHistory(it)}><IconHistory size={18} /></ActionIcon></Tooltip>
        <Tooltip label={t("stock.edit")}><ActionIcon variant="subtle" onClick={() => openEdit(it)}><IconPencil size={18} /></ActionIcon></Tooltip>
        <Tooltip label={t("stock.delete")}><ActionIcon variant="subtle" color="red" onClick={() => void removeItem(it)}><IconTrash size={18} /></ActionIcon></Tooltip>
      </Group>
    );
  }

  function openCreate() { setDraft(EMPTY); editH.open(); }
  function openEdit(it: StockItem) { setDraft({ ...it }); editH.open(); }

  async function saveDraft() {
    try {
      if (!draft.name?.trim()) { notifications.show({ color: "red", message: t("stock.nameRequired") }); return; }
      if (draft.id) await api.updateStock(draft.id, draft);
      else await api.createStock(draft);
      notifications.show({ color: "green", message: t("stock.saved") });
      editH.close();
      await load();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function removeItem(it: StockItem) {
    if (!window.confirm(t("stock.deleteConfirm", { name: it.name }))) return;
    try {
      await api.deleteStock(it.id);
      notifications.show({ color: "green", message: t("stock.deleted") });
      await load();
    } catch (e) {
      const code = e instanceof ApiError ? (e.detail as { error?: string } | null)?.error : undefined;
      notifications.show({
        color: "red",
        message: code === "in_use" ? t("stock.itemInUse") : e instanceof ApiError ? e.message : String(e),
      });
    }
  }

  function openMove(it: StockItem, mode: "restock" | "adjust") {
    setMoveItem(it); setMoveMode(mode);
    setMoveQty(mode === "adjust" ? it.quantity : 0); setMoveNote("");
    moveH.open();
  }
  async function applyMove() {
    if (!moveItem) return;
    try {
      if (moveMode === "restock") await api.restockStock(moveItem.id, moveQty, moveNote || undefined);
      else await api.adjustStock(moveItem.id, moveQty, moveNote || undefined);
      notifications.show({ color: "green", message: t("stock.saved") });
      moveH.close();
      await load();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function openHistory(it: StockItem) {
    setHistItem(it);
    setMovements([]);
    histH.open();
    try {
      const r = await api.stockMovements(it.id);
      setMovements(r.movements);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  function openPrint(it: StockItem) { setPrintItem(it); setPrintCopies(1); }
  async function handlePrintLabel() {
    if (!printItem) return;
    const copies = Math.max(1, Math.min(200, Math.floor(Number(printCopies)) || 1));
    setPrinting(true);
    try {
      if (localAgent.godex) {
        const r = await api.godexPrintStockItemPayload(printItem.id, copies);
        await localAgentPrint(r.ezpl_b64, localAgent.selectedId);
      } else {
        await api.godexPrintStockItem(printItem.id, copies);
      }
      notifications.show({ color: "green", message: t("stock.labelSent", { count: copies }) });
      setPrintItem(null);
    } catch (err) {
      const msg = err instanceof ApiError ? JSON.stringify(err.detail).slice(0, 200)
        : err instanceof Error ? err.message : t("common.error");
      notifications.show({ color: "red", title: t("stock.printFailed"), message: msg });
    } finally {
      setPrinting(false);
    }
  }

  async function recompute() {
    try {
      const r = await api.recomputeStock();
      notifications.show({ color: "green", message: t("stock.recomputeDone", { count: r.fixed }) });
      await load();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  return (
    <Container size="lg">
      <Group justify="space-between" mb="md">
        <Title order={2}>{t("stock.title")}</Title>
        <Group gap="xs">
          {isAdmin && (
            <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={() => void recompute()}>
              {t("stock.recompute")}
            </Button>
          )}
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>{t("stock.add")}</Button>
        </Group>
      </Group>

      <Tabs defaultValue="items" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="items">{t("stock.tabItems")}</Tabs.Tab>
          <Tabs.Tab value="recipes">{t("stock.tabRecipes")}</Tabs.Tab>
          <Tabs.Tab value="consumption">{t("stock.tabConsumption")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="items">
      <ResponsiveTable
        mobile={{
          rows: items,
          rowKey: (it) => it.id,
          title: (it) => <>{it.name}{!it.active ? " ⏸" : ""}</>,
          subtitle: (it) => `${it.quantity} ${it.unit}`,
          badge: (it) => (it.is_low ? <Badge color="red" size="xs">{t("stock.lowBadge")}</Badge> : null),
          fields: [{ label: t("stock.colThreshold"), render: (it) => it.low_threshold ?? "—" }],
          actions: (it) => itemActions(it),
          loading: loading && items.length === 0,
          empty: t("stock.empty"),
        }}
      >
      <Table.ScrollContainer minWidth={600}>
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("stock.colName")}</Table.Th>
              <Table.Th>{t("stock.colQty")}</Table.Th>
              <Table.Th>{t("stock.colThreshold")}</Table.Th>
              <Table.Th>{t("stock.colUnit")}</Table.Th>
              <Table.Th>{t("stock.colActions")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {!loading && items.length === 0 && (
              <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">{t("stock.empty")}</Text></Table.Td></Table.Tr>
            )}
            {items.map((it) => (
              <Table.Tr key={it.id} opacity={it.active ? 1 : 0.5}>
                <Table.Td>{it.name}{!it.active ? " ⏸" : ""}</Table.Td>
                <Table.Td>
                  <Group gap={6}>
                    <Text c={it.is_low ? "red" : undefined} fw={it.is_low ? 700 : undefined}>{it.quantity}</Text>
                    {it.is_low && <Badge color="red" size="xs">{t("stock.lowBadge")}</Badge>}
                  </Group>
                </Table.Td>
                <Table.Td>{it.low_threshold ?? "—"}</Table.Td>
                <Table.Td>{it.unit}</Table.Td>
                <Table.Td>
                  {itemActions(it)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      </ResponsiveTable>
        </Tabs.Panel>
        <Tabs.Panel value="recipes"><RecipesTab canEdit={canEdit} isAdmin={isAdmin} /></Tabs.Panel>
        <Tabs.Panel value="consumption"><ConsumptionTab /></Tabs.Panel>
      </Tabs>

      <Modal opened={editOpen} onClose={editH.close} title={draft.id ? t("stock.edit") : t("stock.add")}>
        <Stack>
          <TextInput label={t("stock.name")} value={draft.name ?? ""} onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })} required />
          <TextInput label={t("stock.unit")} value={draft.unit ?? "buc"} onChange={(e) => setDraft({ ...draft, unit: e.currentTarget.value })} />
          {!draft.id && (
            <NumberInput label={t("stock.quantity")} value={draft.quantity ?? 0} onChange={(v) => setDraft({ ...draft, quantity: Number(v) || 0 })} />
          )}
          <NumberInput label={t("stock.threshold")} min={0} value={draft.low_threshold ?? ""} onChange={(v) => setDraft({ ...draft, low_threshold: v === "" || v === undefined ? null : Number(v) })} />
          <Textarea label={t("stock.notes")} value={draft.notes ?? ""} onChange={(e) => setDraft({ ...draft, notes: e.currentTarget.value })} />
          {draft.id && <Switch label={t("stock.active")} checked={draft.active ?? true} onChange={(e) => setDraft({ ...draft, active: e.currentTarget.checked })} />}
          <Group justify="flex-end">
            <Button variant="default" onClick={editH.close}>{t("stock.cancel")}</Button>
            <Button onClick={() => void saveDraft()}>{t("stock.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={moveOpen} onClose={moveH.close} title={moveItem ? `${moveMode === "restock" ? t("stock.restock") : t("stock.adjust")} — ${moveItem.name}` : ""}>
        <Stack>
          <NumberInput
            label={moveMode === "restock" ? t("stock.restockQty") : t("stock.adjustQty")}
            value={moveQty}
            onChange={(v) => setMoveQty(Number(v) || 0)}
          />
          <TextInput label={t("stock.note")} value={moveNote} onChange={(e) => setMoveNote(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={moveH.close}>{t("stock.cancel")}</Button>
            <Button onClick={() => void applyMove()}>{t("stock.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={printItem !== null} onClose={() => setPrintItem(null)} title={printItem ? `${t("stock.printLabel")} — ${printItem.name}` : ""}>
        <Stack>
          <NumberInput
            label={t("stock.printCopies")}
            min={1}
            max={200}
            allowDecimal={false}
            allowNegative={false}
            value={printCopies}
            onChange={(v) => setPrintCopies(v === "" ? "" : Number(v))}
          />
          <Text size="xs" c="dimmed">
            {localAgent.godex ? t("stock.printViaAgent") : t("stock.printViaServer")}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPrintItem(null)}>{t("stock.cancel")}</Button>
            <Button leftSection={<IconPrinter size={16} />} loading={printing} onClick={() => void handlePrintLabel()}>{t("stock.print")}</Button>
          </Group>
        </Stack>
      </Modal>

      <Drawer opened={histOpen} onClose={histH.close} position="right" title={histItem ? `${t("stock.history")} — ${histItem.name}` : ""}>
        <Table>
          <Table.Thead>
            <Table.Tr><Table.Th>{t("stock.colQty")}</Table.Th><Table.Th>Δ</Table.Th><Table.Th>{t("stock.note")}</Table.Th></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {movements.map((m) => (
              <Table.Tr key={m.id}>
                <Table.Td>{m.balance_after}</Table.Td>
                <Table.Td c={m.delta < 0 ? "red" : "green"}>{m.delta > 0 ? `+${m.delta}` : m.delta}</Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">{new Date(m.created_at).toLocaleString()} · {t(`stock.movementReason.${m.reason}`, { defaultValue: m.reason })}</Text>
                  {m.note && <Text size="xs">{m.note}</Text>}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Drawer>
    </Container>
  );
}
